import type { ReactNode } from 'react'
import { motion, useReducedMotion } from 'motion/react'
import { GlassSurface } from '../GlassSurface'
import { MeshHeroBackground } from '../MeshHeroBackground'

const EASE = [0.22, 1, 0.36, 1] as const

// Phosphor "file-md" — a spec/markdown file.
function FileMdIcon() {
  return (
    <svg viewBox="0 0 256 256" className="h-[18px] w-[18px]" fill="currentColor" aria-hidden>
      <path d="M213.66,82.34l-56-56A8,8,0,0,0,152,24H56A16,16,0,0,0,40,40v72a8,8,0,0,0,16,0V40h88V88a8,8,0,0,0,8,8h48V224a8,8,0,0,0,16,0V88A8,8,0,0,0,213.66,82.34ZM160,51.31,188.69,80H160ZM144,144H128a8,8,0,0,0-8,8v56a8,8,0,0,0,8,8h16a36,36,0,0,0,0-72Zm0,56h-8V160h8a20,20,0,0,1,0,40Zm-40-48v56a8,8,0,0,1-16,0V177.38L74.55,196.59a8,8,0,0,1-13.1,0L48,177.38V208a8,8,0,0,1-16,0V152a8,8,0,0,1,14.55-4.59L68,178.05l21.45-30.64A8,8,0,0,1,104,152Z" />
    </svg>
  )
}

// Phosphor "bug".
function BugIcon() {
  return (
    <svg viewBox="0 0 256 256" className="h-[18px] w-[18px]" fill="currentColor" aria-hidden>
      <path d="M144,92a12,12,0,1,1,12,12A12,12,0,0,1,144,92ZM100,80a12,12,0,1,0,12,12A12,12,0,0,0,100,80Zm116,64A87.76,87.76,0,0,1,213,167l22.24,9.72A8,8,0,0,1,232,192a7.89,7.89,0,0,1-3.2-.67L207.38,182a88,88,0,0,1-158.76,0L27.2,191.33A7.89,7.89,0,0,1,24,192a8,8,0,0,1-3.2-15.33L43,167A87.76,87.76,0,0,1,40,144v-8H16a8,8,0,0,1,0-16H40v-8a87.76,87.76,0,0,1,3-23L20.8,79.33a8,8,0,1,1,6.4-14.66L48.62,74a88,88,0,0,1,158.76,0l21.42-9.36a8,8,0,0,1,6.4,14.66L213,89.05a87.76,87.76,0,0,1,3,23v8h24a8,8,0,0,1,0,16H216ZM56,120H200v-8a72,72,0,0,0-144,0Zm64,95.54V136H56v8A72.08,72.08,0,0,0,120,215.54ZM200,144v-8H136v79.54A72.08,72.08,0,0,0,200,144Z" />
    </svg>
  )
}

// The Claude Code mark, drawn as an outline so it matches the other line icons.
function ClaudeCodeMark() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-[18px] w-[18px]"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M20.998 10.949H24v3.102h-3v3.028h-1.487V20H18v-2.921h-1.487V20H15v-2.921H9V20H7.488v-2.921H6V20H4.487v-2.921H3V14.05H0V10.95h3V5h17.998v5.949z" />
      <path
        d="M6 10.949h1.488V8.102H6v2.847zm10.51 0H18V8.102h-1.49v2.847z"
        fill="currentColor"
        stroke="none"
      />
    </svg>
  )
}

const ADVANTAGES: { icon: ReactNode; title: string; detail: string }[] = [
  {
    icon: <FileMdIcon />,
    title: 'Spec-driven agent building',
    detail: 'Turn your requirements into agent tests automatically.',
  },
  {
    icon: <ClaudeCodeMark />,
    title: 'Speed up development',
    detail: 'Set up a self-improving agent loop.',
  },
  {
    icon: <BugIcon />,
    title: 'Replicate and fix issues from production',
    detail: 'Turn a production trace into a simulation and prove the fix.',
  },
]

export function SectionWhy() {
  const prefersReducedMotion = useReducedMotion() ?? false

  const rise = (delay: number) =>
    prefersReducedMotion
      ? { initial: false as const, animate: { opacity: 1, y: 0 } }
      : ({
          initial: { opacity: 0, y: 8 },
          whileInView: { opacity: 1, y: 0 },
          // Positive bottom margin so the section starts animating in while it's
          // still approaching from below the fold, instead of waiting until it's
          // 80px inside the viewport (which read as "doesn't fade in until
          // you've scrolled quite far past it").
          viewport: { once: true, margin: '0px 0px 200px 0px' },
          transition: { duration: 0.55, ease: EASE, delay },
        } as const)

  return (
    <section id="why" className="relative scroll-mt-0 bg-paper">
      <div className="container-rail pt-16 pb-24 md:pt-28 md:pb-32">
        <div className="mx-auto max-w-[1120px]">
          <motion.h2
            {...rise(0.06)}
            id="why-heading"
            className="text-[26px] font-semibold leading-[1.14] tracking-[-0.02em] text-ink-950 md:text-[33px]"
          >
            <span className="text-ink-500">
              AI agents are still tested by hand, breaking in production.
            </span>
            <br />
            LangWatch brings{' '}
            <span className="text-rainbow-shift">loop engineering</span> to agent
            testing and evaluation.
          </motion.h2>

          <div className="mt-20 grid gap-12 md:grid-cols-2 md:items-start md:gap-16 md:mt-24">
            {/* Left: the problem, then what changes with LangWatch */}
            <div>
              <motion.div
                {...rise(0.09)}
                className="max-w-[520px] space-y-4 text-[16px] leading-[1.7] text-ink-600"
              >
                <p>
                  An agent can take a hundred paths to the same goal, testing
                  them by hand catches only a few.
                </p>
                <p>
                  The best teams run agent simulations as continuous testing and
                  evaluation, so reliability climbs every release.
                </p>
              </motion.div>

              <div className="mt-10 space-y-7">
                {ADVANTAGES.map((item, i) => (
                  <motion.div
                    key={item.title}
                    {...rise(0.12 + i * 0.06)}
                    className="flex items-start gap-4"
                  >
                    <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-white/70 bg-white/50 text-brand-700 shadow-[inset_0_1px_1px_rgba(255,255,255,0.9),inset_0_0_0_0.5px_rgba(255,255,255,0.5),0_6px_14px_-6px_rgba(20,20,23,0.18)] backdrop-blur-md">
                      {item.icon}
                    </span>
                    <div>
                      <p className="text-[16px] font-semibold tracking-tight text-ink-950">
                        {item.title}
                      </p>
                      <p className="mt-1 max-w-[460px] text-[14.5px] leading-[1.55] text-ink-500">
                        {item.detail}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Right: the loop — specs → simulations → agent improvement,
                built from the real liquid-glass pills over our mesh field. */}
            <motion.figure {...rise(0.12)}>
              <LoopDiagram />
              <figcaption className="sr-only">
                Specs to simulations to agent improvement, repeating as a
                continuous loop.
              </figcaption>
            </motion.figure>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ---------------- loop diagram ---------------- */

// Loop arrows + advantage icons.
const ACCENT = '#d97757'

// Morph targets for the fluid blob (objectBoundingBox), built from layered sine
// harmonics: a smooth, irregular outline with no hard lobes and no straight
// runs. Radius never reaches the 0..1 edges, so nothing clips flat; the whole
// thing is rendered onto an oversized layer and rotated for the "fluid" feel.
const BLOB_PATHS = [
  'M0.93,0.5 C0.93,0.537 0.926,0.576 0.917,0.612 C0.908,0.648 0.897,0.686 0.878,0.718 C0.859,0.75 0.831,0.78 0.802,0.802 C0.773,0.824 0.735,0.837 0.702,0.849 C0.669,0.861 0.635,0.868 0.601,0.876 C0.567,0.884 0.535,0.895 0.5,0.899 C0.465,0.903 0.427,0.908 0.393,0.901 C0.359,0.894 0.323,0.877 0.295,0.856 C0.267,0.835 0.246,0.802 0.226,0.774 C0.206,0.746 0.193,0.716 0.176,0.687 C0.159,0.658 0.139,0.632 0.123,0.601 C0.107,0.57 0.087,0.536 0.079,0.5 C0.071,0.464 0.069,0.423 0.076,0.386 C0.083,0.349 0.1,0.313 0.119,0.28 C0.138,0.247 0.162,0.216 0.189,0.189 C0.216,0.162 0.247,0.135 0.28,0.118 C0.314,0.101 0.353,0.089 0.39,0.088 C0.427,0.087 0.466,0.098 0.5,0.11 C0.534,0.122 0.563,0.144 0.592,0.158 C0.621,0.172 0.647,0.183 0.677,0.194 C0.707,0.205 0.743,0.21 0.774,0.226 C0.805,0.242 0.841,0.262 0.865,0.289 C0.889,0.316 0.908,0.353 0.919,0.388 C0.93,0.423 0.93,0.463 0.93,0.5Z',
  'M0.931,0.5 C0.93,0.538 0.926,0.575 0.916,0.611 C0.906,0.647 0.893,0.685 0.872,0.715 C0.851,0.745 0.822,0.772 0.792,0.792 C0.763,0.813 0.727,0.825 0.695,0.838 C0.663,0.851 0.633,0.862 0.6,0.873 C0.568,0.884 0.535,0.898 0.5,0.903 C0.466,0.908 0.427,0.91 0.393,0.901 C0.36,0.892 0.326,0.871 0.299,0.849 C0.272,0.827 0.252,0.796 0.23,0.77 C0.208,0.744 0.189,0.718 0.168,0.691 C0.147,0.664 0.121,0.638 0.103,0.606 C0.085,0.574 0.067,0.537 0.061,0.5 C0.056,0.463 0.06,0.422 0.07,0.385 C0.08,0.349 0.1,0.313 0.121,0.281 C0.142,0.249 0.166,0.219 0.194,0.194 C0.222,0.169 0.253,0.144 0.287,0.13 C0.321,0.116 0.361,0.111 0.396,0.111 C0.432,0.112 0.468,0.124 0.5,0.133 C0.532,0.142 0.559,0.155 0.59,0.163 C0.621,0.171 0.651,0.172 0.685,0.179 C0.719,0.187 0.759,0.191 0.792,0.208 C0.825,0.225 0.859,0.25 0.881,0.28 C0.903,0.31 0.917,0.349 0.925,0.386 C0.933,0.423 0.933,0.463 0.931,0.5Z',
  'M0.927,0.5 C0.925,0.537 0.921,0.575 0.91,0.61 C0.899,0.645 0.883,0.681 0.861,0.709 C0.84,0.737 0.809,0.761 0.781,0.781 C0.753,0.801 0.721,0.815 0.691,0.831 C0.661,0.847 0.633,0.863 0.601,0.876 C0.569,0.889 0.535,0.906 0.5,0.91 C0.465,0.914 0.426,0.911 0.393,0.9 C0.36,0.889 0.329,0.865 0.302,0.844 C0.275,0.823 0.254,0.795 0.229,0.771 C0.205,0.747 0.179,0.726 0.155,0.699 C0.131,0.672 0.101,0.644 0.084,0.611 C0.067,0.578 0.053,0.538 0.051,0.5 C0.049,0.462 0.059,0.421 0.072,0.385 C0.085,0.349 0.106,0.316 0.128,0.285 C0.15,0.254 0.174,0.225 0.202,0.202 C0.23,0.179 0.263,0.158 0.296,0.146 C0.329,0.134 0.367,0.132 0.401,0.132 C0.435,0.132 0.468,0.142 0.5,0.146 C0.532,0.15 0.56,0.152 0.593,0.154 C0.626,0.156 0.662,0.15 0.698,0.156 C0.734,0.162 0.778,0.17 0.81,0.19 C0.842,0.21 0.871,0.242 0.89,0.275 C0.909,0.308 0.918,0.349 0.924,0.386 C0.93,0.424 0.929,0.463 0.927,0.5Z',
  'M0.919,0.5 C0.917,0.536 0.913,0.573 0.901,0.607 C0.89,0.641 0.871,0.675 0.85,0.702 C0.829,0.729 0.799,0.751 0.772,0.772 C0.745,0.793 0.718,0.811 0.69,0.83 C0.662,0.849 0.635,0.87 0.603,0.884 C0.571,0.898 0.535,0.914 0.5,0.916 C0.465,0.918 0.426,0.911 0.393,0.898 C0.36,0.886 0.331,0.861 0.303,0.841 C0.275,0.821 0.25,0.799 0.223,0.777 C0.196,0.755 0.165,0.735 0.139,0.708 C0.114,0.681 0.085,0.65 0.07,0.615 C0.055,0.58 0.048,0.538 0.05,0.5 C0.052,0.462 0.067,0.423 0.082,0.388 C0.097,0.353 0.118,0.321 0.14,0.292 C0.162,0.263 0.186,0.235 0.213,0.213 C0.241,0.191 0.273,0.173 0.305,0.162 C0.337,0.151 0.373,0.149 0.405,0.146 C0.438,0.143 0.468,0.147 0.5,0.145 C0.532,0.143 0.563,0.135 0.598,0.133 C0.634,0.131 0.676,0.124 0.713,0.131 C0.751,0.138 0.793,0.153 0.823,0.177 C0.853,0.201 0.876,0.239 0.891,0.274 C0.907,0.309 0.911,0.351 0.916,0.389 C0.921,0.427 0.922,0.464 0.919,0.5Z',
  'M0.93,0.5 C0.93,0.537 0.926,0.576 0.917,0.612 C0.908,0.648 0.897,0.686 0.878,0.718 C0.859,0.75 0.831,0.78 0.802,0.802 C0.773,0.824 0.735,0.837 0.702,0.849 C0.669,0.861 0.635,0.868 0.601,0.876 C0.567,0.884 0.535,0.895 0.5,0.899 C0.465,0.903 0.427,0.908 0.393,0.901 C0.359,0.894 0.323,0.877 0.295,0.856 C0.267,0.835 0.246,0.802 0.226,0.774 C0.206,0.746 0.193,0.716 0.176,0.687 C0.159,0.658 0.139,0.632 0.123,0.601 C0.107,0.57 0.087,0.536 0.079,0.5 C0.071,0.464 0.069,0.423 0.076,0.386 C0.083,0.349 0.1,0.313 0.119,0.28 C0.138,0.247 0.162,0.216 0.189,0.189 C0.216,0.162 0.247,0.135 0.28,0.118 C0.314,0.101 0.353,0.089 0.39,0.088 C0.427,0.087 0.466,0.098 0.5,0.11 C0.534,0.122 0.563,0.144 0.592,0.158 C0.621,0.172 0.647,0.183 0.677,0.194 C0.707,0.205 0.743,0.21 0.774,0.226 C0.805,0.242 0.841,0.262 0.865,0.289 C0.889,0.316 0.908,0.353 0.919,0.388 C0.93,0.423 0.93,0.463 0.93,0.5Z',
]

function LoopDiagram() {
  const reduce = useReducedMotion() ?? false
  return (
    <div className="relative mx-auto aspect-square w-full max-w-[460px]">
      {/* an organic clip: the identity mesh becomes one fluid blob that morphs
          between shapes and slowly turns, like a slow water splash */}
      <svg width="0" height="0" aria-hidden className="absolute">
        <defs>
          <clipPath id="whyBlobClip" clipPathUnits="objectBoundingBox">
            <path d={BLOB_PATHS[0]}>
              {!reduce && (
                <>
                  <animate
                    attributeName="d"
                    dur="30s"
                    repeatCount="indefinite"
                    calcMode="spline"
                    keyTimes="0;0.25;0.5;0.75;1"
                    keySplines="0.45 0 0.55 1;0.45 0 0.55 1;0.45 0 0.55 1;0.45 0 0.55 1"
                    values={BLOB_PATHS.join(';')}
                  />
                  <animateTransform
                    attributeName="transform"
                    type="rotate"
                    from="0 0.5 0.5"
                    to="360 0.5 0.5"
                    dur="48s"
                    repeatCount="indefinite"
                  />
                </>
              )}
            </path>
          </clipPath>
        </defs>
      </svg>

      {/* mesh rendered onto an oversized layer so the blob has room to bulge
          past the box (and rotate) without ever clipping flat on an edge */}
      <div
        className="absolute inset-[-12%]"
        style={{ clipPath: 'url(#whyBlobClip)', WebkitClipPath: 'url(#whyBlobClip)' }}
      >
        <MeshHeroBackground
          protectSide="none"
          colors={['#ffffff', '#ffe6d2', '#ffffff', '#ffffff']}
          staticOnly
        />
      </div>

      {/* brighten the centre so the pill labels keep their contrast */}
      <div
        className="pointer-events-none absolute left-1/2 top-1/2 h-[60%] w-[60%] -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.55), rgba(255,255,255,0) 70%)' }}
      />

      {/* the loop arrows, clockwise: specs → simulations → agent improvement → specs */}
      <svg
        viewBox="0 0 400 400"
        className="absolute inset-0 z-[1] h-full w-full"
        fill="none"
        aria-hidden
      >
        <defs>
          <marker
            id="whyArrowHead"
            viewBox="0 0 10 10"
            refX="6.5"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto"
          >
            <path
              d="M0,1 L9,5 L0,9"
              fill="none"
              stroke={ACCENT}
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </marker>
        </defs>
        {/* nodes sit equidistant on a circle (120 deg apart), so all three
            arcs are identical in length — reads as a circle, not a triangle */}
        <g stroke={ACCENT} strokeWidth="2.4" strokeLinecap="round" markerEnd="url(#whyArrowHead)">
          {/* specs → simulations, down the right */}
          <path d="M269,80.5 A138,138 0 0 1 338,200" />
          {/* simulations → agent improvement, across the bottom */}
          <path d="M269,319.5 A138,138 0 0 1 131,319.5" />
          {/* agent improvement → specs, up the left */}
          <path d="M62,200 A138,138 0 0 1 131,80.5" />
        </g>
      </svg>

      {/* the three liquid-glass pills */}
      <GlassPill label="Specs" left="50%" top="18%" />
      <GlassPill label="Simulations" left="77.75%" top="66%" />
      <GlassPill
        label={
          <>
            Agent
            <br />
            Improvement
          </>
        }
        left="22.25%"
        top="66%"
      />
    </div>
  )
}

function GlassPill({
  label,
  left,
  top,
}: {
  label: ReactNode
  left: string
  top: string
}) {
  return (
    <div
      className="absolute z-10 -translate-x-1/2 -translate-y-1/2"
      style={{ left, top }}
    >
      <div className="relative grid place-items-center rounded-full px-5 py-2.5">
        <GlassSurface />
        <span className="relative z-10 text-center text-[13.5px] font-semibold leading-[1.15] tracking-tight text-ink-900 md:text-[15px]">
          {label}
        </span>
      </div>
    </div>
  )
}
